import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import { filter, first, map, Observable, of, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  getInputWorkflowIdForElement,
} from '@blaze-runtime/engine-domain';
import { WorkflowId } from '@blaze-shared/api-contracts-domain';
import { createAliasForAggregation } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { WorkflowSchema } from '@blaze-shared/schema-domain';
import { Workflow } from '@blaze-shared/workflow-domain';

import { hasProps } from '../../canvas-elements/element-model/element-props-guards';
import { ChartPropsModel } from '../../elements';
import { WorkflowCrudService } from '../../services/workflow-crud.service';
import { getElementById } from '../elements';

import {
  prepareNodesForLineChartsMutably,
  prepareNodesForPieChartsMutably,
  prepareNodesForSingleValueChartsMutably,
} from './charts.utils';

export interface ChartWorkflowPreparationData {
  element: CanvasElement;
  workflow?: Workflow;
  schema?: WorkflowSchema;
  columnsAlreadyInDataset: string[];
}

@Injectable({ providedIn: 'root' })
export class ChartsFacade {
  private readonly store = inject(Store);
  private readonly schemaService = inject(SchemaService);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  private addColumnsFromDataset = (data: ChartWorkflowPreparationData): Observable<any> => {
    if (!data || !data.schema) {
      return of({ ...data, columnsAlreadyInDataset: [] });
    }

    const columnsAlreadyInDataset = ['computeByYear', 'computeByMonth', 'computeByDetails'].filter(
      (computedColName) => data.schema?.columns.find((col) => col.name === computedColName),
    );

    return of({ ...data, columnsAlreadyInDataset });
  };

  getChartWorkflowData(elementId: CanvasElementId): Observable<ChartWorkflowPreparationData> {
    const element$ = this.store.select(getElementById(elementId)).pipe(
      first(),
      map((element) => {
        if (!element) {
          throw new Error(`Element with id ${elementId} was not found`);
        }
        return element;
      }),
    );

    const workflowData$ = element$.pipe(
      switchMap((element) => this.getWorkflowDataForElement(element)),
    );

    return workflowData$.pipe(filter(this.isValidChartData), switchMap(this.addColumnsFromDataset));
  }

  prepareAndUpdateWorkflow(data: ChartWorkflowPreparationData): Observable<Workflow> {
    if (!hasProps<ChartPropsModel>(data.element)) {
      throw new Error('Chart element props missing');
    }

    const props = data.element?.props as ChartPropsModel;
    const workflow = produce(data.workflow as Workflow, (draft) => {
      this.applyWorkflowChanges(draft, data.element, props, data.columnsAlreadyInDataset);
      delete draft.isDraft;
    });
    return this.workflowCrudService.updateWorkflow(workflow);
  }

  private getWorkflowDataForElement(
    element: CanvasElement,
  ): Observable<ChartWorkflowPreparationData> {
    return this.store.select(runtimeSelectors.selectConnections).pipe(
      first(),
      switchMap((connections) => {
        const workflowId = getInputWorkflowIdForElement(connections, element.id);
        if (!workflowId) {
          throw new Error(`Input workflow id for element ${element.id} was not found`);
        }

        return this.getWorkflowById(workflowId).pipe(
          map((workflow) => ({
            element,
            workflow,
            schema: this.schemaService.getWorkflowSchema(workflow.id),
            columnsAlreadyInDataset: [],
          })),
        );
      }),
    );
  }

  private getWorkflowById(workflowId: WorkflowId): Observable<Workflow> {
    return this.store.select(runtimeSelectors.selectWorkflowById(workflowId)).pipe(first(Boolean));
  }

  private isValidChartData(data: ChartWorkflowPreparationData): boolean {
    if (!data || !data.workflow || !data.element) {
      return false;
    }

    const { mainValue, aggregation } =
      (hasProps<ChartPropsModel>(data.element) && data.element.props.content?.data) || {};

    return !!((mainValue && aggregation) || aggregation === 'COUNT');
  }

  private applyWorkflowChanges(
    workflow: Workflow,
    element: CanvasElement,
    props: ChartPropsModel,
    columnsAlreadyInDataset: string[],
  ): void {
    switch (element?.type) {
      case 'chartSingle':
        this.prepareSingleValueChart(workflow, props, columnsAlreadyInDataset);
        break;
      case 'chartDot':
      case 'chartLine':
      case 'chartBar':
      case 'chartColumn':
        this.prepareLineOrBarChart(workflow, props, columnsAlreadyInDataset);
        break;
      case 'chartDoughnut':
      case 'chartPie':
        this.preparePieChart(workflow, props, columnsAlreadyInDataset);
        break;
    }
  }

  private prepareSingleValueChart(
    workflow: Workflow,
    props: ChartPropsModel,
    columnsAlreadyInDataset: string[],
  ): void {
    const { mainValue, aggregation, alias } = props.content.data || {};

    prepareNodesForSingleValueChartsMutably(
      workflow,
      {
        chartType: 'chartSingle',
        aggregation,
        mainValue,
        alias,
      },
      columnsAlreadyInDataset,
    );
  }

  private prepareLineOrBarChart(
    workflow: Workflow,
    props: ChartPropsModel,
    columnsAlreadyInDataset: string[],
  ): void {
    const {
      mainValue,
      groupBy,
      groupByDetails,
      aggregation,
      segmentData,
      segmentDataDetails,
      alias,
    } = props.content.data || {};

    if ((!mainValue && aggregation !== 'COUNT') || !groupBy || !aggregation) {
      return;
    }

    const additionalAggregationsArray = props.content.data?.multiSeries
      ?.map((data, index) => {
        const alias = createAliasForAggregation(data, index + 1, undefined, undefined);
        return { ...data, alias };
      })
      .filter((series, index) => {
        return (
          index !== 0 &&
          ((series.mainValue && series.aggregation) || series.aggregation === 'COUNT')
        );
      });

    prepareNodesForLineChartsMutably(workflow, {
      chartType: 'chartLine',
      mainValue,
      groupBy,
      aggregation,
      segmentData,
      groupByDetails,
      segmentDataDetails,
      alias: props.content.data?.multiSeries?.length
        ? createAliasForAggregation({ alias }, 0, undefined, undefined)
        : 'chartAggregation',
      additionalAggregationsArray,
      columnsAlreadyInDataset,
    });
  }

  private preparePieChart(
    workflow: Workflow,
    props: ChartPropsModel,
    columnsAlreadyInDataset: string[],
  ): void {
    const { mainValue, aggregation, segmentData, segmentDataDetails, alias } =
      props.content.data || {};

    if ((!mainValue && aggregation !== 'COUNT') || !aggregation || !segmentData) {
      return;
    }

    const additionalAggregationsArray = props.content.data?.multiSeries?.filter((series, index) => {
      return (
        index !== 0 && ((series.mainValue && series.aggregation) || series.aggregation === 'COUNT')
      );
    });

    prepareNodesForPieChartsMutably(workflow, {
      chartType: 'chartPie',
      mainValue,
      aggregation,
      segmentData,
      segmentDataDetails,
      alias,
      additionalAggregationsArray,
      columnsAlreadyInDataset,
    });
  }
}
